import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  FormHelperText,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { usePost, usePut } from "../../../hooks/api-call";
import useCustomToast from "../../../hooks/use-toast";
import OrderBuyerSection from "./OrderBuyerSection";
import OrderSellerSection from "./OrderSellerSection";
import { convertMillisecondsToDate } from "../../../hooks/helper";
import * as Yup from "yup";
import { useYupValidationResolver } from "../../../hooks/use-validation-resolver";
import GstTypeSelector from "../../../Components/GstTypeSelector";

const validationSchema = Yup.object().shape({
  poNumber: Yup.string().required("PO Number is required"),
  poDate: Yup.date().required("PO Date is required").test(
    "poDate-validation",
    "PO Date must be earlier than Expected Delivery Date",
    function (value) {
      const { expectedDeliveryDate } = this.parent;
      if (expectedDeliveryDate && value) {
        return new Date(value) < new Date(expectedDeliveryDate);
      }
      return true;
    }
  ),
  expectedDeliveryDate: Yup.date()
    .nullable()
    .min(new Date(), "Expected Delivery Date must be in the future")
    .test(
      "expectedDeliveryDate-validation",
      "Expected Delivery Date must be later than PO Date",
      function (value) {
        const { poDate } = this.parent;
        if (poDate && value) {
          return new Date(value) > new Date(poDate);
        }
        return true;
      }
    ),
  materialId: Yup.string().required("Material is required"),
  buyerId: Yup.string().required("Buyer is required"),
  sellerId: Yup.string().required("Seller is required"),
  mineralPPT: Yup.string().required("Mineral price per ton is required"),
  freightPPT: Yup.string().required("Freight price per ton is required"),
  poType: Yup.string().required("PO Type is required"),
  mineralIgstPrcntId: Yup.number()
    .nullable()
    .when("gstMineralType", {
      is: "IGST",
      then: (schema) => schema.required("IGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  mineralCgstPrcntId: Yup.number()
    .nullable()
    .when("gstMineralType", {
      is: "CGST_SGST",
      then: (schema) => schema.required("CGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  mineralSgstPrcntId: Yup.number()
    .nullable()
    .when("gstMineralType", {
      is: "CGST_SGST",
      then: (schema) => schema.required("SGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  freightIgstPrcntId: Yup.number()
    .nullable()
    .when("gstFreightType", {
      is: "IGST",
      then: (schema) => schema.required("IGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  freightCgstPrcntId: Yup.number()
    .nullable()
    .when("gstFreightType", {
      is: "CGST_SGST",
      then: (schema) => schema.required("CGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  freightSgstPrcntId: Yup.number()
    .nullable()
    .when("gstFreightType", {
      is: "CGST_SGST",
      then: (schema) => schema.required("SGST is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

});

const OrderDrawer = ({
  setIsDrawerOpen,
  fetchOrdersData,
  selectedOrderForEdit,
  setSelectedOrderForEdit,
  minerals
}) => {
  const { showSuccessToast } = useCustomToast();
  const { postData } = usePost();
  const { putData } = usePut();
  const [selectedMineral, setSelectedMineral] = useState({});

  const resolver = useYupValidationResolver(validationSchema);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues, watch, resetField,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm({

    resolver,
    mode: "onBlur",
    defaultValues: selectedOrderForEdit,
  });

  const onClose = () => {
    setIsDrawerOpen(false);
    setSelectedOrderForEdit(null);
    reset();
  };

  const handleSubmitForm = async (data) => {
    try {
      if (selectedOrderForEdit) {
        await putData(
          data,
          `order/${selectedOrderForEdit?.orderId}?buyerId=${data.buyerId}&sellerId=${data.sellerId}&materialId=${data.materialId}`
        );
        showSuccessToast("Success", "Order updated successfully");
      } else {
        await postData(
          data,
          `order?buyerId=${data.buyerId}&sellerId=${data.sellerId}&materialId=${data.materialId}`
        );
        showSuccessToast("Success", "Order created successfully");
      }
      fetchOrdersData("0", {});
      onClose();
    } catch (err) {
      console.log("errorr", err)
    }

  };

  const handleSelectedMineral = (e) => {
    const mineralId = e.target.value;
    setValue("materialId", mineralId);
    const resp = minerals.find((f) => +f.materialId === +mineralId);
    if (resp) {
      setSelectedMineral(resp);
    }
  };

  useEffect(() => {
    if (selectedOrderForEdit) {      
      const {
        poNumber,
        totalQuantity,
        price,
        orderStatus,
        material,
        buyer,
        seller,
        poDate,
        expectedDeliveryDate,
        mineralPpt,
        freightPpt, 
        freightIgstPrcntId,
        mineralIgstPrcntId,
        mineralSgstPrcntId,
        mineralCgstPrcntId,
        freightSgstPrcntId,
        freightCgstPrcntId
      } = selectedOrderForEdit;
      const isMineralIgst = mineralIgstPrcntId ? 'IGST' : 'CGST_SGST';
      const isFreightIgst = freightIgstPrcntId ? 'IGST' : 'CGST_SGST';

      setValue("poNumber", poNumber);
      setValue("poDate", convertMillisecondsToDate(poDate));
      setValue("expectedDeliveryDate", convertMillisecondsToDate(expectedDeliveryDate));
      setValue("materialId", material.materialId);
      setValue("buyerId", buyer.buyerId);
      setValue("sellerId", seller.sellerId);
      setValue("totalQuantity", totalQuantity);
      setValue("price", price);
      setValue("mineralPPT",mineralPpt);
      setValue("freightPPT",freightPpt);
      setValue("orderStatus", orderStatus);
      setValue("gstMineralType",isMineralIgst)
      setValue("gstFreightType",isFreightIgst)
      setValue("mineralIgstPrcntId",mineralIgstPrcntId?.gstPercentageId);
      setValue("mineralSgstPrcntId",mineralSgstPrcntId?.gstPercentageId);
      setValue("mineralCgstPrcntId",mineralCgstPrcntId?.gstPercentageId);
      setValue("freightIgstPrcntId",freightIgstPrcntId?.gstPercentageId);
      setValue("freightSgstPrcntId",freightSgstPrcntId?.gstPercentageId);
      setValue("freightCgstPrcntId",freightCgstPrcntId?.gstPercentageId);
      const data = minerals.find((m) => m.materialId === material.materialId);
      setSelectedMineral(data);
    }
  }, [selectedOrderForEdit]);

  return (
    <Drawer anchor="right" open={true} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: '90%', md: '70%' } } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Typography variant="h6">
          {selectedOrderForEdit ? "Edit Order" : "New Order"}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit(handleSubmitForm)} p={3}>
        <Typography variant="subtitle1" gutterBottom>Order Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="poNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="PO Number"
                  fullWidth
                  type="text"
                  error={!!errors.poNumber}
                  helperText={errors.poNumber?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="poDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="PO Date"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.poDate}
                  helperText={errors.poDate?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="expectedDeliveryDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Expected Delivery Date"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.expectedDeliveryDate}
                  helperText={errors.expectedDeliveryDate?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth error={!!errors.materialId}>
              <InputLabel>Minerals</InputLabel>
              <Controller
                name="materialId"
                control={control}
                render={({ field }) => (
                  <Select {...field} onChange={handleSelectedMineral} label="Minerals">
                    {minerals.map((mineral) => (
                      <MenuItem key={mineral.materialId} value={mineral.materialId}>
                        {mineral.materialName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.materialId?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Category" value={selectedMineral?.categoryName || ""} fullWidth disabled />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="totalQuantity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Total Quantity"
                  fullWidth
                  type="number"
                  error={!!errors.totalQuantity}
                  helperText={errors.totalQuantity?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="mineralPPT"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Mineral Price Per Ton"
                  fullWidth
                  type="number"
                  error={!!errors.mineralPrice}
                  helperText={errors.mineralPrice?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="freightPPT"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Freight Price Per Ton"
                  fullWidth
                  type="number"
                  error={!!errors.freigthPrice}
                  helperText={errors.freigthPrice?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth error={!!errors.poType}>
              <InputLabel>PO Type</InputLabel>
              <Controller
                name="poType"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Status">
                    <MenuItem value="FOR">FOR</MenuItem>
                    <MenuItem value="FOB">FOB</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText>{errors.poType?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth error={!!errors.orderStatus}>
              <InputLabel>Status</InputLabel>
              <Controller
                name="orderStatus"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Status">
                    <MenuItem value="created">Created</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText>{errors.orderStatus?.message}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Box mt={3}>
          <GstTypeSelector labelPrefix={'mineral'} control={control} setValue={setValue} watch={watch} errors={errors} />
        </Box>
        <Box mt={3}>
          <GstTypeSelector labelPrefix={'freight'} control={control} setValue={setValue} watch={watch} errors={errors} />
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle1">Buyer Information</Typography>
          <OrderBuyerSection
            errors={errors}
            control={control}
            initialValue={selectedOrderForEdit?.buyer}
            onChange={(e) => setValue("buyerId", e.target.value)}
          />
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle1">Seller Information</Typography>
          <OrderSellerSection
            errors={errors}
            control={control}
            initialValue={selectedOrderForEdit?.seller}
            onChange={(e) => setValue("sellerId", e.target.value)}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
          disabled={!isDirty || !isValid}
          >
            {selectedOrderForEdit ? "Update Order" : "Add Order"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default OrderDrawer;
